import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CountryDropdown from "../../../shared/forms/CountriesDropDown";
import { US_STATES, CANADA_PROVINCES } from "../../../utils/States";
import { normalizeTradeShowUrl } from "./TradeShowsHelpers";
import { TRADE_SHOW_ATTENDEES_ENABLED } from "./featureFlags";

const BRAND_BLUE = "#00336C";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function ManualWorkspaceForm({ onCreateWorkspace }) {
  const [manualShowName, setManualShowName] = useState("");
  const [manualMonth, setManualMonth] = useState("");
  const [manualYear, setManualYear] = useState("");
  const [manualCity, setManualCity] = useState("");
  const [manualState, setManualState] = useState("");
  const [manualCountry, setManualCountry] = useState("");
  const [manualIndustry, setManualIndustry] = useState("");
  const [manualDirectoryUrl, setManualDirectoryUrl] = useState("");
  const [attendeeDirectoryUrl, setAttendeeDirectoryUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const stateOptions = manualCountry === "United States" ? US_STATES : manualCountry === "Canada" ? CANADA_PROVINCES : [];

  async function handleManualCreate() {
    const name = manualShowName.trim();
    if (!name || creating) return;
    const directoryUrl = normalizeTradeShowUrl(manualDirectoryUrl);
    setCreating(true);
    setError("");
    try {
      await onCreateWorkspace({
        workspaceName: [name, manualMonth, manualYear.trim()].filter(Boolean).join(" "),
        tradeShowName: name,
        month: manualMonth,
        year: manualYear.trim(),
        city: manualCity.trim(),
        state: manualState,
        country: manualCountry.trim(),
        location: [manualCity.trim(), manualState, manualCountry.trim()].filter(Boolean).join(", "),
        industry: manualIndustry.trim(),
        auto_import_exhibitors: Boolean(directoryUrl),
        ...(TRADE_SHOW_ATTENDEES_ENABLED ? { attendeeDirectoryUrl: normalizeTradeShowUrl(attendeeDirectoryUrl) } : {}),
        selectedTradeShow: directoryUrl ? {
          source: "manual",
          name,
          industry: manualIndustry.trim(),
          exhibitor_directory_url: directoryUrl,
        } : null,
      });
      setManualShowName("");
      setManualMonth("");
      setManualYear("");
      setManualCity("");
      setManualState("");
      setManualCountry("");
      setManualIndustry("");
      setManualDirectoryUrl("");
      setAttendeeDirectoryUrl("");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to create the trade show workspace.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box sx={{ mt: 3, p: { xs: 2, md: 2.5 }, border: "1px solid #dce4ef", borderRadius: 2, backgroundColor: "#f7f9fc" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: "grid", placeItems: "center", flexShrink: 0, backgroundColor: "rgba(0,51,108,0.09)", color: BRAND_BLUE }}>
            <AddCircleOutlineIcon fontSize="small" />
          </Box>
          <Typography sx={{ fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.3 }}>
            Create a Workspace Manually
          </Typography>
        </Stack>
        <Button
          variant="contained"
          disabled={!manualShowName.trim() || creating}
          onClick={handleManualCreate}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
          sx={{ flexShrink: 0, whiteSpace: "nowrap", textTransform: "none", fontWeight: 800, backgroundColor: BRAND_BLUE, boxShadow: "none", "&:hover": { backgroundColor: "#002752", boxShadow: "none" } }}
        >
          Create Manually
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Typography sx={{ fontSize: "0.84rem", color: "#5a6a80", mb: 2.5, maxWidth: 760 }}>
        Add the show details now. Upload a CSV or XLSX exhibitor list after creation, or provide a supported directory URL for automatic import.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
        <TextField label="Trade show name" value={manualShowName} onChange={(event) => setManualShowName(event.target.value)} required size="small" fullWidth sx={{ backgroundColor: "#fff" }} />
        <TextField label="Month" value={manualMonth} onChange={(event) => setManualMonth(event.target.value)} select SelectProps={{ native: true }} size="small" fullWidth sx={{ backgroundColor: "#fff" }}>
          <option value="" />
          {MONTHS.map((month) => <option key={month} value={month}>{month}</option>)}
        </TextField>
        <TextField label="Year" value={manualYear} onChange={(event) => setManualYear(event.target.value)} size="small" inputProps={{ maxLength: 4 }} fullWidth sx={{ backgroundColor: "#fff" }} />
        <CountryDropdown label="Country" value={manualCountry} onChange={(event) => { setManualCountry(event.target.value); setManualState(""); }} size="small" sx={{ backgroundColor: "#fff" }} />
        <TextField label="State/Province" value={manualState} onChange={(event) => setManualState(event.target.value)} select SelectProps={{ native: true }} disabled={!stateOptions.length} size="small" fullWidth sx={{ backgroundColor: "#fff" }}>
          <option value="" />
          {stateOptions.map((state) => <option key={state.code} value={state.name}>{state.name}</option>)}
        </TextField>
        <TextField label="City" value={manualCity} onChange={(event) => setManualCity(event.target.value)} size="small" fullWidth sx={{ backgroundColor: "#fff" }} />
        <TextField label="Industry" value={manualIndustry} onChange={(event) => setManualIndustry(event.target.value)} size="small" fullWidth sx={{ backgroundColor: "#fff" }} />
        <TextField label="Exhibitor directory URL (optional)" value={manualDirectoryUrl} onChange={(event) => setManualDirectoryUrl(event.target.value)} size="small" fullWidth sx={{ gridColumn: { md: "span 2" }, backgroundColor: "#fff" }} />
        {TRADE_SHOW_ATTENDEES_ENABLED && <TextField label="Attendee list URL (optional)" value={attendeeDirectoryUrl} onChange={(event) => setAttendeeDirectoryUrl(event.target.value)} size="small" fullWidth sx={{ backgroundColor: "#fff" }} helperText="Public attendee CSV or table; imports after creation." />}
      </Box>
    </Box>
  );
}
